import type { ImageLoadFailureKind } from '@/types/canvas-type';

const DEFAULT_PROBE_TIMEOUT_MS = 5000;
const RETRYABLE_HTTP_STATUS = new Set([408, 429]);
const ALLOWED_IMAGE_PROTOCOLS = new Set(['http:', 'https:', 'blob:', 'data:']);

interface ImageFailureProbeResponse {
  status: number;
}

interface ImageLoadFailureProbeOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  fetcher?: (input: string, init: RequestInit) => Promise<ImageFailureProbeResponse>;
  isOnline?: () => boolean;
  baseUrl?: string;
}

function isTimeoutError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'TimeoutError';
}

function resolveImageUrl(url: string, baseUrl?: string): URL | null {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return null;

  try {
    const fallbackBase =
      baseUrl ?? (typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
    const resolved = new URL(trimmedUrl, fallbackBase);
    return ALLOWED_IMAGE_PROTOCOLS.has(resolved.protocol) ? resolved : null;
  } catch {
    return null;
  }
}

export function classifyImageHttpStatus(status: number): ImageLoadFailureKind {
  if (RETRYABLE_HTTP_STATUS.has(status) || status >= 500) return 'network';
  return 'permanent';
}

/**
 * Pixi 加载失败后补充探测 HTTP 状态。
 * 无法确认的 CORS/未知异常保守归为 permanent，避免把路径或配置问题误报为网络问题。
 */
export async function classifyImageLoadFailure(
  url: string,
  originalError: unknown,
  options: ImageLoadFailureProbeOptions = {}
): Promise<ImageLoadFailureKind | null> {
  if (options.signal?.aborted) return null;
  if (isTimeoutError(originalError)) return 'network';

  const resolvedUrl = resolveImageUrl(url, options.baseUrl);
  if (!resolvedUrl) return 'permanent';
  if (resolvedUrl.protocol === 'blob:' || resolvedUrl.protocol === 'data:') return 'permanent';

  const isOnline =
    options.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  if (!isOnline()) return 'network';

  const fetcher =
    options.fetcher ??
    (typeof fetch === 'undefined'
      ? undefined
      : (input: string, init: RequestInit) => fetch(input, init));
  if (!fetcher) return 'permanent';

  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS);
  const abortFromParent = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromParent, { once: true });

  try {
    let response = await fetcher(resolvedUrl.href, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal
    });

    // 少数资源服务器不支持 HEAD；只在这种情况下用单字节 GET 补探测。
    if (response.status === 405 || response.status === 501) {
      response = await fetcher(resolvedUrl.href, {
        method: 'GET',
        cache: 'no-store',
        headers: { Range: 'bytes=0-0' },
        signal: controller.signal
      });
    }

    return classifyImageHttpStatus(response.status);
  } catch {
    if (options.signal?.aborted) return null;
    if (timedOut || !isOnline()) return 'network';
    return 'permanent';
  } finally {
    globalThis.clearTimeout(timeoutId);
    options.signal?.removeEventListener('abort', abortFromParent);
  }
}
